import React, { useMemo } from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
  Progress,
  Badge,
  Table
} from 'reactstrap';
import { Line, Bar, Doughnut, Area } from 'react-chartjs-2';
import DateFilter from './DateFilter';
import moment from 'moment';

const OverviewTab = ({
  dispatch,
  mrrAnalytics,
  revenueAnalytics,
  subscriptionStats,
  plansOverview,
  dateFilter,
  selectedRange,
  onDateChange,
  getRevenueAnalytics,
  getMRRAnalytics,
  getSubscriptionStats,
  getPlansOverview
}) => {

  const RANGE_LABELS = {
    '1d': '1 Day', '7d': 'Last 7 days', '30d': 'Last 30 days', '4w': 'Last 4 weeks',
    'mtd': 'Month to date', '3m': 'Last 3 months', '6m': 'Last 6 months',
    '12m': 'Last 12 months', 'all': 'All time'
  };

  const revenueChartData = useMemo(() => ({
    labels: revenueAnalytics?.chartData?.labels || [],
    datasets: [
      { label: 'Subscription Revenue', data: revenueAnalytics?.chartData?.subscriptionRevenue || [],
        borderColor: '#2563EB', backgroundColor: 'rgba(37, 99, 235, 0.1)', fill: true, tension: 0.4 },
      { label: 'One-time Revenue', data: revenueAnalytics?.chartData?.onetimeRevenue || [],
        borderColor: '#10B981', backgroundColor: 'rgba(16, 185, 129, 0.1)', fill: true, tension: 0.4 }
    ]
  }), [revenueAnalytics?.chartData]);

  const mrrChartData = useMemo(() => ({
    labels: ['New MRR', 'Expansion MRR', 'Churned MRR', 'Net MRR'],
    datasets: [{
      label: 'MRR Components',
      data: [mrrAnalytics?.newMRR || 0, mrrAnalytics?.expansionMRR || 0,
             -(Math.abs(mrrAnalytics?.churnedMRR || 0)), mrrAnalytics?.netNewMRR || 0],
      backgroundColor: ['#10B981', '#2563EB', '#EF4444', '#8B5CF6'],
      borderRadius: 8
    }]
  }), [mrrAnalytics]);

  const planChartData = useMemo(() => ({
    labels: plansOverview?.planDistribution?.labels || ['No Data'],
    datasets: [{
      data: plansOverview?.planDistribution?.data || [0],
      backgroundColor: plansOverview?.planDistribution?.colors || ['#94A3B8'],
      borderWidth: 2, borderColor: '#fff'
    }]
  }), [plansOverview?.planDistribution]);

  // Calculate key metrics
  const totalRevenue = revenueAnalytics?.totalRevenue || 0;
  const activeSubscriptions = subscriptionStats?.activeCount || 0;
  const churnRate = parseFloat(subscriptionStats?.churnRate) || 0;
  const avgSubscriptionValue = subscriptionStats?.averageValue || 0;

  return (
    <div className=''>
      {/* Header with Date Filter */}
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <h4 className='mb-0'>Billing & Revenue Analytics</h4>
        <DateFilter
          selectedRange={RANGE_LABELS[selectedRange] || 'Last 7 days'}
          onDateChange={onDateChange}
        />
      </div>

      {/* Key Metrics Cards */}
      <div className='counter-sections mb-3'>
        <div className='counter-box style-two'>
          <div className='counter-start' >
            <div className='left-text-area'>
              <h5>Monthly Recurring Revenue</h5>
              <div className='CountUp-box'>
                <h3 className="" style={{ color: '#9333ea' }}>${(mrrAnalytics?.currentMRR || 0).toLocaleString()}</h3>
              </div>
              <small className={mrrAnalytics?.mrrGrowth >= 0 ? 'text-success' : 'text-danger'}>
                <i className={`fa fa-arrow-${mrrAnalytics?.mrrGrowth >= 0 ? 'up' : 'down'} mr-1`}></i>
                {Math.abs(mrrAnalytics?.mrrGrowth || 0).toFixed(1)}%
              </small>
            </div>
            <div className='right-icon'>
              <span className='icon'><i className='fa fa-chart-line text-primary' style={{ fontSize: '20px' }}></i></span>
            </div>
          </div>
        </div>

        <div className='counter-box style-two'>
          <div className='counter-start' >
            <div className='left-text-area'>
              <h5>Total Revenue</h5>
              <div className='CountUp-box'>
                <h3 className="text-success">${totalRevenue.toLocaleString()}</h3>
              </div>
              <small className='text-muted'>
                Period total
              </small>
            </div>
            <div className='right-icon'>
              <span className='icon'><i className='fa fa-dollar-sign text-success' style={{ fontSize: '18px' }}></i></span>
            </div>
          </div>
        </div>

        <div className='counter-box style-two'>
          <div className='counter-start' >
            <div className='left-text-area'>
              <h5>Active Subscriptions</h5>
              <div className='CountUp-box'>
                <h3 className="" style={{ color: '#EF4D56' }}>{activeSubscriptions}</h3>
              </div>
            </div>
            <small className={subscriptionStats?.subscriptionGrowth >= 0 ? 'text-success' : 'text-danger'}>
              <i className={`fa fa-arrow-${subscriptionStats?.subscriptionGrowth >= 0 ? 'up' : 'down'} mr-1`}></i>
              {Math.abs(subscriptionStats?.subscriptionGrowth || 0).toFixed(1)}%
            </small>
            <div className='right-icon'>
              <span className='icon'><i className='fa fa-users text-warning' style={{ fontSize: '18px' }}></i></span>
            </div>
          </div>
        </div>

        <div className='counter-box style-two'>
          <div className='counter-start' >
            <div className='left-text-area'>
              <h5>Churn Rate</h5>
              <div className='CountUp-box'>
                <h3 className="" style={{ color: '#9333EA' }}>{churnRate.toFixed(1)}%</h3>
              </div>
              <small className={churnRate <= 5 ? 'text-success' : churnRate <= 10 ? 'text-warning' : 'text-danger'}>
                {churnRate <= 5 ? 'Healthy' : churnRate <= 10 ? 'Monitor' : 'High'}
              </small>
            </div>
            <div className='right-icon'>
              <span className='icon'><i className='fa fa-user-times text-warning' style={{ fontSize: '18px' }}></i></span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className='chart-box'>
        <div className='row'>
          {/* Revenue Trend Chart */}
          <div className='col-lg-8 col-md-12'>
            <div className='chart-area'>
              <h4>Revenue Trend</h4>
              <div style={{ height: '350px' }}>
                <Line
                  key={`revenue-chart-${selectedRange}-${JSON.stringify(revenueChartData.labels)}`}
                  data={revenueChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                      intersect: false,
                      mode: 'index'
                    },
                    plugins: {
                      legend: {
                        position: 'top',
                        labels: {
                          usePointStyle: true,
                          padding: 20
                        }
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            let label = context.dataset.label || '';
                            if (label) {
                              label += ': ';
                            }
                            if (context.parsed.y !== null) {
                              label += new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD'
                              }).format(context.parsed.y);
                            }
                            return label;
                          }
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: function (value) {
                            return '$' + value.toLocaleString();
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
          {/* Subscription Distribution */}
          <div className='col-lg-4 col-md-12'>
            <div className='chart-area'>
              <h4>Plan Distribution</h4>
              <div style={{ height: '350px' }}>
                <Doughnut
                  key={`plan-chart-${selectedRange}-${JSON.stringify(planChartData.datasets[0].data)}`}
                  data={planChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          padding: 15,
                          usePointStyle: true,
                          font: {
                            size: 11
                          }
                        }
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            const label = context.label || '';
                            const value = context.parsed || 0;
                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                            const percentage = ((value / total) * 100).toFixed(1);
                            return `${label}: ${value} (${percentage}%)`;
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
          {/* MRR Breakdown */}
          <div className='col-lg-6 col-md-12'>
            <div className='chart-area'>
              <h4>MRR Breakdown</h4>
              <div style={{ height: '300px' }}>
                <Bar
                  key={`mrr-chart-${selectedRange}-${mrrAnalytics?.currentMRR || 0}`}
                  data={mrrChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            const value = Math.abs(context.parsed.y);
                            return `${context.label}: $${value.toLocaleString()}`;
                          }
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: function (value) {
                            return '$' + Math.abs(value).toLocaleString();
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
          {/* Customer Metrics */}
          <div className='col-lg-6 col-md-12'>
            <div className='chart-area'>
              <h4>Customer Metrics</h4>
              <div className='metrics-area'>
                <div className='row'>
                  <div className='col-lg-6 col-md-12'>
                    <div className='metric-item'>
                      <label className='text-muted small'>Avg. Revenue per User</label>
                      <h4>${avgSubscriptionValue.toFixed(2)}</h4>
                    </div>
                  </div>
                  <div className='col-lg-6 col-md-12'>
                    <div className='metric-item'>
                      <label className='text-muted small'>Customer Lifetime Value</label>
                      <h4>${(subscriptionStats?.ltv || 0).toFixed(2)}</h4>
                    </div>
                  </div>
                </div>
                <div className='metric-item'>
                  <label className='text-muted small'>Trial Conversion Rate</label>
                  <h4>{(subscriptionStats?.trialConversionRate || 0).toFixed(1)}%</h4>
                  <Progress value={subscriptionStats?.trialConversionRate || 0} color="primary" className='mt-2' />
                </div>
                <div className='metric-item'>
                  <label className='text-muted small'>Payment Success Rate</label>
                  <h4>{(subscriptionStats?.paymentSuccessRate || 0).toFixed(1)}%</h4>
                  <Progress value={subscriptionStats?.paymentSuccessRate || 0} color="success" className='mt-2' />
                </div>
              </div>
            </div>
          </div>
          <div className='col-12'>
            <div className='chart-area'>
              <div className='table-area table-workspaces'>
                <h4>Top Performing Plans</h4>
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Plan Name</th>
                      <th>Active Users</th>
                      <th>Monthly Revenue</th>
                      <th>Growth Rate</th>
                      <th>Churn Rate</th>
                      <th>ARPU</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plansOverview?.plans?.slice(0, 5).map(plan => (
                      <tr key={plan._id}>
                        <td>
                          <strong>{plan.displayName || plan.name}</strong>
                          {plan.popularPlan && <Badge color='success' className='ms-2'>Popular</Badge>}
                        </td>
                        <td>{plan.metrics?.activeSubscribers || 0}</td>
                        <td>${(plan.metrics?.monthlyRevenue || 0).toLocaleString()}</td>
                        <td>
                          <span className={plan.metrics?.growthRate > 0 ? 'text-success' : plan.metrics?.growthRate < 0 ? 'text-danger' : ''}>
                            {plan.metrics?.growthRate > 0 ? '+' : ''}{plan.metrics?.growthRate || 0}%
                          </span>
                        </td>
                        <td>
                          <span className={plan.metrics?.churnRate > 5 ? 'text-danger' : 'text-success'}>
                            {plan.metrics?.churnRate || 0}%
                          </span>
                        </td>
                        <td>${plan.metrics?.averageRevenue || plan.price || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;